import { Search, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// Use public path for preload compatibility
const heroImage = "/images/hero-image-votre-premier-clic.webp";

const jobSearchSchema = z.object({
  metier: z.string().trim().max(100).optional(),
  localisation: z.string().trim().max(100).optional(),
});

const Hero = () => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(jobSearchSchema),
  });

  const onSubmit = (data: z.infer<typeof jobSearchSchema>) => {
    // Redirection vers le site emplois du Réseau Eureka
    const params = new URLSearchParams();
    if (data.metier) params.set("keywords", data.metier);
    if (data.localisation) params.set("localization", data.localisation);
    const query = params.toString();
    window.open(`https://emplois.reseau-eureka.fr/${query ? `?${query}` : ""}`, "_blank");
  };

  return (
    <section className="relative min-h-[600px] lg:min-h-[700px] flex items-center">
      {/* Background Image - Using img tag for better LCP discovery */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={heroImage}
          alt=""
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="sync"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 gradient-hero opacity-90"></div>
      </div>

      {/* Content */}
      <div className="container relative z-10 py-16 lg:py-24">
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-8 animate-fade-in">
            Votre premier clic<br />
            vers <span className="text-accent">l'emploi !</span>
          </h1>

          {/* Search Box */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-primary/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-primary-foreground/20 shadow-xl">
              <div className="space-y-1">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Métier, mot-clé"
                        className={`pl-10 h-12 bg-background/95 border-primary-foreground/20 ${errors.metier ? "border-destructive" : ""}`}
                        {...register("metier")}
                      />
                    </div>
                    <div className="h-6 mt-1">
                      {errors.metier?.message && (
                        <p className="text-destructive text-sm">{String(errors.metier.message)}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Ville, département"
                        className="pl-10 h-12 bg-background/95 border-primary-foreground/20"
                        {...register("localisation")}
                      />
                    </div>
                    <div className="h-6 mt-1"></div>
                  </div>
                </div>
                <Button type="submit" size="lg" className="w-full md:w-auto h-12 px-8 bg-secondary hover:bg-secondary/90 text-secondary-foreground">
                  Trouver mon emploi
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Hero;
